"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { OmnibusManager } from "@/lib/OmnibusManager.client";
import { DatabaseManager } from "@/lib/database/DatabaseManager.client";
import Notification from "@/components/Notification";

export default function StartPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const manager = new OmnibusManager();
  const session = DatabaseManager.createBrowserClient();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Function to complete pending invitations
  const completePendingInvitations = async (userEmail: string) => {
    try {
      const response = await fetch('/api/invitations/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userEmail }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.completed > 0) {
          setNotification({ type: "success", message: `Completed ${data.completed} pending invitation(s) for ${userEmail}` });
        }
      }
    } catch (error) {
      setNotification({ type: "error", message: 'Error completing pending invitations' });
    }
  };

  /* ---------- session guard ---------- */
  useEffect(() => {
    const check = async () => {
      await new Promise((r) => setTimeout(r, 100));

      // verified user fetch (cookie-aware)
      const user = await manager.auth.getCurrentUser()

      if (user) {
        window.location.replace("/");
      } else {
        setCheckingSession(false);
      }
    };

    check();

    const { data: sub } = session.auth.onAuthStateChange(async (evt, session) => {
      if (evt === "SIGNED_IN" && session?.user) {
        // Complete any pending invitations for this user
        if (session.user.email) {
          await completePendingInvitations(session.user.email);
        }
        window.location.replace("/");
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [router, session, searchParams]);

  /* ---------- handle URL parameters ---------- */
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const messageParam = searchParams.get('message');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (messageParam === 'account_exists') {
      setMessage('An account with this email already exists. Please sign in instead.');
    }
  }, [searchParams]);

  /* ---------- login submit ---------- */
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error } = await session.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    }
    // on success, onAuthStateChange will redirect and complete invitations
  }

  /* ---------- ui ---------- */
  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Checking session…</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-4 text-center">Sign In</h1>
      
      {message && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm">
          {message}
        </div>
      )}
      
      {notification && (
        <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}

      <form onSubmit={handleLogin} className="space-y-4">
          <label className="block">
            <span className="text-sm">Email</span>
            <input
              className="mt-1 block w-full border rounded px-3 py-2"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-sm">Password</span>
            <input
              className="mt-1 block w-full border rounded px-3 py-2"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            disabled={loading}
            className="rounded-xl bg-sky-600 px-8 py-4 text-white font-semibold shadow hover:bg-sky-700 transition w-full disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

      <div className="text-right mt-2">
        <a
          href={email ? `/auth/forgot-password?email=${encodeURIComponent(email)}` : "/auth/forgot-password"}
          className="text-sky-600 hover:underline text-sm"
        >
          Forgot password?
        </a>
      </div>

      <p className="text-sm text-center text-gray-600 mt-4">
        Need access? Contact your administrator for an invitation.
      </p>
    </div>
  );
}
